import React from 'react';
import { DateRange, UTC } from 'rewire-common';
import { observable, Observe } from 'rewire-core';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Queries from '../graphql/Queries';
import { ISearchFn, nameSearcher } from '../utility/Searchers';
import { makeStyles, Theme } from '../utility/styles';

const model = observable({
  search: async (_text: string) => {},
  items: [] as any[],
});

const styles = (_theme: Theme) => ({
  popper: {
    width: '1100px !important',
    maxWidth: '100vh',
    maxHeight: '80vh',
  },
  listbox: {
    padding: 10,
    fontSize: '12px',
    maxHeight: '80vh',
  },
  headingContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(75px, 100%))',
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: '#14809d',
    fontSize: '14px',
    padding: '4px',
  },
  option: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(75px, 100%))',
    paddingLeft: '4px',
    paddingRight: '4px',
  },
});

const useStyles = makeStyles(styles);

const ListboxComponent = React.forwardRef((props: any, ref: any) => {
  const { children, ...rest } = props;
  const s = useStyles();
  return (
    <ul {...rest} ref={ref}>
      <div className={s.headingContainer}>
        <div>Area</div>
        <div>Group</div>
        <div>Occupation</div>
        <div>Shift Type</div>
        <div>Shift Pattern</div>
      </div>
      {children}
    </ul>
  );
});

function optionsAreEqual(option1: any, option2: any) {
  return (
    option1?.area?.id === option2?.area?.id &&
    option1?.group?.id === option2?.group?.id &&
    option1?.occupation?.id === option2?.occupation?.id &&
    option1?.template?.id === option2?.template?.id &&
    option1?.generator?.id === option2?.generator?.id
  );
}

function filterOptions(options: any, _state: any) {
  return options.filter((value: any, index: number, self: any) => index === self.findIndex((t: any) => optionsAreEqual(t, value)));
}

function isOptionSelected(option: any, value: any) {
  return optionsAreEqual(option, value);
}

function getOptionLabel(option?: any) {
  return `${option?.template?.name}`;
}

export default function ScheduleRuleContextSearch(onChange: (evt: any, value: any) => void, getEarnedDate: () => UTC) {
  const performScheduleRuleContextSearch = (search: string) => {
    const schema = `
      job { id position fields}
      area { id name }
      group { id name fields}
      occupation { id name }
      template { id name }
      generator { id name }
    `;
    const date = getEarnedDate();
    return Queries.scheduleSearch(schema, search, new DateRange(date, date), 25);
  };

  const searcher = nameSearcher(performScheduleRuleContextSearch as ISearchFn);

  model.search = async (text: string) => {
    const items = (await searcher.search(text)) as any[];
    model.items.set(items);
  };

  return () => {
    React.useEffect(() => {
      return () => {
        model.search = async (_text: string) => {};
        model.items = [];
      };
    }, []);
    const s = useStyles();
    return (
      <Observe
        render={() => (
          <Autocomplete
            size="small"
            fullWidth={true}
            classes={{ popper: s.popper, listbox: s.listbox, option: s.option }}
            filterOptions={filterOptions}
            getOptionLabel={getOptionLabel}
            getOptionSelected={isOptionSelected}
            clearOnBlur={true}
            onChange={(evt, value) => onChange(evt, value)}
            onInputChange={(_evt: any, value: string) => model.search(value)}
            options={model.items.map((i) => i)}
            ListboxComponent={ListboxComponent as any}
            renderOption={(option) => {
              return (
                <>
                  <div>{option.area?.name ?? ''}</div>
                  <div>{option.group?.name ?? ''}</div>
                  <div>{option.occupation?.name ?? ''}</div>
                  <div>{option.template?.name ?? ''}</div>
                  <div>{option.generator?.name ?? ''}</div>
                </>
              );
            }}
            renderInput={(params) => (
              <Observe
                render={() => {
                  return (
                    <TextField
                      {...params}
                      label="Schedule Rule Shift"
                      variant="outlined"
                      placeholder="enter schedule rule shift"
                      InputProps={{ ...params.InputProps }}
                      InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                    />
                  );
                }}
              />
            )}
          />
        )}
      />
    );
  };
}
